import type { RelationNode, RelationLink } from "@/mocks/gossip";

const kindStyle: Record<RelationNode["kind"], string> = {
  core: "border-primary-400 bg-primary-500 text-background-50",
  ally: "border-accent-300 bg-accent-100 text-accent-900",
  opponent: "border-secondary-400 bg-secondary-100 text-secondary-900",
  neutral: "border-background-300 bg-background-100 text-foreground-800",
};

const kindLabel: Record<RelationNode["kind"], string> = {
  core: "核心",
  ally: "关联",
  opponent: "对立",
  neutral: "中性",
};

const toneStyle: Record<RelationLink["tone"], string> = {
  positive: "bg-accent-100 text-accent-800",
  conflict: "bg-secondary-200 text-secondary-900",
  neutral: "bg-background-200 text-foreground-700",
};

type Props = {
  nodes: RelationNode[];
  links: RelationLink[];
};

export default function RelationshipMap({ nodes, links }: Props) {
  const core = nodes.find((n) => n.kind === "core") ?? nodes[0];
  const others = nodes.filter((n) => n.id !== core.id);

  const linkFromCore = (id: string) => links.find((l) => l.from === core.id && l.to === id);

  const lgCols = others.length >= 4 ? "lg:grid-cols-4" : others.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2";

  return (
    <div className="w-full">
      <div className="flex flex-col items-center">
        <div
          className={`w-full max-w-[320px] rounded-lg border-2 px-5 py-4 text-center ${kindStyle[core.kind]}`}
        >
          <span className="font-label text-[11px] opacity-80">核心主体</span>
          <p className="mt-1 font-heading font-bold text-lg leading-snug">{core.name}</p>
          <p className="mt-0.5 font-label text-xs opacity-80">{core.role}</p>
        </div>

        <span className="block w-px h-8 bg-background-300" aria-hidden="true"></span>

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${lgCols} gap-4 w-full`}>
          {others.map((n) => {
            const link = linkFromCore(n.id);
            return (
              <div key={n.id} className="flex flex-col items-center">
                <span className="block w-px h-4 bg-background-300" aria-hidden="true"></span>
                <div className="flex w-full flex-col items-center gap-2 text-center">
                  {link && (
                    <span className={`rounded-full px-2.5 py-0.5 font-label text-[11px] ${toneStyle[link.tone]}`}>
                      {link.label}
                    </span>
                  )}
                  <div className={`w-full rounded-lg border px-3.5 py-3 ${kindStyle[n.kind]}`}>
                    <span className="font-label text-[10px] opacity-75">{kindLabel[n.kind]}</span>
                    <p className="font-heading font-semibold text-[15px] leading-snug">{n.name}</p>
                    <p className="mt-0.5 font-label text-[11px] opacity-80 leading-snug">{n.role}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}